import { useTranslation } from 'react-i18next';

import { CloseCircleIcon, ModalShell, PlayButtonIcon } from '../../components';

/**
 * 진단 이어하기 팝업(시안 `1048:48431`) — 진행 중인 회차가 있을 때 홈에 들어서면 뜬다.
 *
 * 시안은 버튼 둘뿐이지만 닫을 길을 둔다: 홈에 머물 길이 없으면 더보기·이력에 못 간다.
 * 닫으면 카드 CTA가 `이어서 진단하기`로 남아 길을 잃지 않는다. 배경 탭만으로는 **보이지
 * 않는 출구**라, 눈에 보이는 X와 Esc를 같이 둔다 — 이어가지도 버리지도 않고 앱을 쓰려는
 * 사람이 고를 수 있는 선택지가 화면에 하나는 있어야 한다.
 *
 * 상태 줄 색은 시안의 파랑 대신 레드다 — 파랑은 다른 화면 팔레트에 없어 SCREENS §10-11이
 * 확인 항목으로 올려 둔 색이다.
 */

interface ResumeDialogProps {
  /** 지금 서 있는 단계(1-기준) — 마친 수 + 1. */
  current: number;
  total: number;
  onRestart: () => void;
  onResume: () => void;
  onDismiss: () => void;
}

export const ResumeDialog = ({
  current,
  total,
  onRestart,
  onResume,
  onDismiss,
}: ResumeDialogProps) => {
  const { t } = useTranslation();

  return (
    <ModalShell
      labelledBy="resume-dialog-title"
      dismissLabel={t('web.home.resume.dismiss')}
      onDismiss={onDismiss}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('web.home.resume.dismiss')}
        className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center text-muted-foreground"
      >
        <CloseCircleIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
        <PlayButtonIcon className="h-6 w-6 text-primary" aria-hidden="true" />
      </div>
      <h2 id="resume-dialog-title" className="mt-4 text-lg font-bold">
        {t('web.home.resume.title')}
      </h2>
      <p className="mt-1 text-sm font-semibold tabular-nums text-primary">
        {t('web.home.resume.status', { current, total })}
      </p>
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
        {t('web.home.resume.body')}
      </p>
      <p className="mt-2 text-sm font-medium text-muted-foreground">
        {t('web.home.resume.restartWarning', { current })}
      </p>
      <div className="mt-6 flex gap-2">
        <button
          type="button"
          onClick={onRestart}
          className="min-h-[48px] flex-1 rounded-full border border-border bg-background text-sm font-semibold"
        >
          {t('web.home.resume.restart')}
        </button>
        <button
          type="button"
          autoFocus
          onClick={onResume}
          className="min-h-[48px] flex-1 rounded-full bg-[var(--bk-900)] text-sm font-semibold text-white"
        >
          {t('web.home.resume.continue_')}
        </button>
      </div>
    </ModalShell>
  );
};
